import React, { Component, createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { compact, get } from 'lodash-es';

import {
  fireHeavyAdInterventionEvent,
  handleAdLoad,
} from 'client/engagement-handlers/ads-engagement-handler/ads-engagement-handler';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { isRenderedAdUnit } from 'client/utils/ads';
import { registerGptSlotV2, reserveGptSlotV2, unregisterGptSlotV2 } from 'client/utils/ads-runtime/gpt-runtime-v2';
import { refreshGptSlots } from 'client/utils/ads-runtime/refresh-gpt-slots';
import {
  registerSlotEventListeners,
  unregisterSlotEventListeners,
} from 'site-modules/shared/components/ads-context/utils/gpt-slot-event-registry';
import { RefreshRules } from './utils/refresh-rules';
import { getPrebidAdUnit } from './utils/header-bidding-config';

import { AdUnitDefaultProps, AdUnitPropTypes } from './ad-unit-prop-types';
import { getAdSizes, getClassNames, getSitename, getTargeting, refreshAdUnit } from './ad-unit-helper-v2';
import { AdsPositionManager } from './ads-position-manager';
import { GPT_NETWORK_CODE } from './context/sitename';

import './ad-unit.scss';

let dynamicAdCount = 0;

export class AdUnitSlotV2 extends Component {
  constructor(props) {
    super(props);

    const { adName, nativeStyle, position } = props;

    this.ref = createRef();
    this.state = {
      isRenderPlaceholderComponent: false,
      isShowLabel: this.props.showLabel,
      lineItemId: '',
    };

    this.adZonePosition = AdsPositionManager.getInstance().getPosition(adName, nativeStyle, position);
  }

  componentDidMount() {
    window.addEventListener('message', this.messageListener, false);
    this.hasMounted = true;
    this.registerSlot();
  }

  componentDidUpdate(prevProps) {
    this.registerSlot();
    const { vehicleContextChange, visitorLocation, adName, refreshDisableWhenZipChanged } = this.props;
    const prevZipCode = prevProps.visitorLocation.zipCode;
    const zipCode = visitorLocation.zipCode;

    if (!refreshDisableWhenZipChanged && !vehicleContextChange && prevZipCode !== zipCode) {
      refreshAdUnit(adName, this.getSlotId());
    }
  }

  componentWillUnmount() {
    this.hasMounted = false;
    window.removeEventListener('message', this.messageListener, false);
    this.unregisterSlot();
  }

  onSlotRegister = event => {
    if (typeof this.props.onSlotRegister === 'function') {
      this.props.onSlotRegister(event);
    }
  };

  onSlotRender = event => {
    const { adName, placeholderComponent, showLabel, slotRenderEndListener, refreshable } = this.props;
    const eventData = {
      ...event,
      slotId: this.getSlotId(),
      adName,
    };

    this.setState({
      lineItemId: eventData.lineItemId,
      isRenderPlaceholderComponent: placeholderComponent && !isRenderedAdUnit(eventData),
      isShowLabel: showLabel && isRenderedAdUnit(eventData),
    });

    if (typeof slotRenderEndListener === 'function') {
      slotRenderEndListener(eventData);
    }

    if (refreshable) {
      RefreshRules.addToRefreshableUnitsArray(this.getSlotId(), this.props);
    }

    handleAdLoad(eventData);

    if (!isRenderedAdUnit(eventData)) {
      this.ref.current?.querySelector('iframe')?.setAttribute('tabindex', '-1');
    }
  };

  onSlotVisibilityChanged = event => {
    const { inViewPercentage } = event;
    if (!this.props.refreshWhenViewable) {
      return;
    }

    if (inViewPercentage === 100 && this.previousVisibility !== 0) {
      if (this.wasShownOnce && this.wasHidden) {
        refreshGptSlots(this.getSlotId());
        this.wasHidden = false;
      }
      this.wasShownOnce = true;
    } else if (inViewPercentage === 0 && this.previousVisibility !== 100) {
      this.wasHidden = true;
    }
    this.previousVisibility = inViewPercentage;
  };

  onSlotIsViewable = event => {
    if (typeof this.props.onSlotIsViewable === 'function') {
      this.props.onSlotIsViewable(event);
    }
  };

  getPosition = () => {
    const { hasAdZone, isIncrementPosition, position } = this.props;
    return hasAdZone && isIncrementPosition ? this.adZonePosition : position;
  };

  getSlotId = () => {
    const { prefixId, adName, nativeStyle, page, customTargeting } = this.props;
    return compact([prefixId, adName, nativeStyle, `${this.getPosition()}`, page.name, get(customTargeting, 'sect8')])
      .join('-')
      .replace(',', '_');
  };

  setSlotElement = element => {
    this.slotElement = element;
    if (element) {
      this.registerSlot();
    } else {
      this.unregisterSlot();
    }
  };

  registerSlot = () => {
    if (!this.hasMounted) {
      return;
    }
    const slotId = this.getSlotId();
    if (this.reservedSlotId !== slotId) {
      this.unregisterSlot();
      this.reservedSlotId = slotId;
      const { nativeStyle, prefixId, adName, page, customTargeting } = this.props;
      this.requestGroup =
        nativeStyle === 'filmstrip'
          ? JSON.stringify([prefixId, adName, nativeStyle, page.name, customTargeting?.sect8])
          : 'page';
      reserveGptSlotV2(slotId, this.requestGroup, {
        atomic: nativeStyle === 'filmstrip',
        expectedCount: this.props.requestGroupSize,
      });
    }
    if (!this.slotElement || this.registeredSlotId) {
      return;
    }

    this.registeredSlotId = slotId;
    const sizes = getAdSizes(this.props);
    const adUnitPath = `/${GPT_NETWORK_CODE}/${getSitename(this.props)}`;
    registerSlotEventListeners({
      slotId,
      onSlotRenderEnded: this.onSlotRender,
      onSlotVisibilityChanged: this.onSlotVisibilityChanged,
      onImpressionViewable: this.onSlotIsViewable,
    });

    registerGptSlotV2({
      slotId,
      requestGroup: this.requestGroup,
      requestGroupSize: this.props.requestGroupSize,
      sizes,
      adUnitPath,
      targetingArguments: getTargeting({
        ...this.props,
        position: this.getPosition(),
      }),
      prebidAdUnit: getPrebidAdUnit({
        adName: this.props.adName,
        slotId,
        adUnitPath,
        sizes,
      }),
    });

    dynamicAdCount += 1;
    this.onSlotRegister({
      slotId,
      sizes,
      slotCount: dynamicAdCount,
      adElementRef: this.ref,
    });
  };

  unregisterSlot = () => {
    if (this.registeredSlotId) {
      unregisterSlotEventListeners(this.registeredSlotId);
    }
    const slotId = this.registeredSlotId || this.reservedSlotId;
    if (slotId) {
      unregisterGptSlotV2(slotId);
    }
    this.registeredSlotId = null;
    this.reservedSlotId = null;
  };

  messageListener = ({ data }) => {
    const { lineItemId } = this.state;
    const messageId = get(data, 'body.id', '');
    const messageLineItemId = get(data, 'lineItemId', '');
    const messageText = get(data, 'body.message', '');

    if (messageId === 'HeavyAdIntervention' && lineItemId) {
      fireHeavyAdInterventionEvent(messageLineItemId, messageText);
    }
  };

  render() {
    const { adName, nativeStyle, placeholderComponent: PlaceholderComponent, placeholderComponentProps } = this.props;
    const { isRenderPlaceholderComponent } = this.state;

    const containerProps = {
      className: getClassNames(this.props, this.state),
      'data-adname': adName,
      'data-native-style': nativeStyle,
    };

    return (
      <Fragment>
        <div {...containerProps} ref={this.ref}>
          <ClientSideRender showTimeout={1}>
            <div className="adunitContainer">
              <div id={this.getSlotId()} className="adBox" ref={this.setSlotElement} />
            </div>
          </ClientSideRender>
        </div>
        {isRenderPlaceholderComponent && <PlaceholderComponent {...placeholderComponentProps} />}
      </Fragment>
    );
  }
}

AdUnitSlotV2.propTypes = {
  ...AdUnitPropTypes,
  prefixId: PropTypes.string,
  hasAdZone: PropTypes.bool,
  isIncrementPosition: PropTypes.bool,
  onSlotRegister: PropTypes.func,
  onSlotIsViewable: PropTypes.func,
  requestGroupSize: PropTypes.number,
};

AdUnitSlotV2.defaultProps = {
  ...AdUnitDefaultProps,
  prefixId: undefined,
  hasAdZone: false,
  isIncrementPosition: false,
  onSlotRegister: null,
  onSlotIsViewable: null,
  requestGroupSize: 0,
};
